'use client';

import React from 'react';
import { LuScale } from 'react-icons/lu';
import { PiCheckCircleBold } from "react-icons/pi";
import Link from 'next/link';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-linear-to-b from-gray-50 to-gray-100">
      {/* Hero Section */}
      <div className="relative h-[30vh] sm:h-[40vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/pages/terms-of-service-hero.jpg"
            alt="Terms of Service"
            className="h-full w-full object-cover object-center brightness-[0.7]"
          />
          <div className="absolute inset-0 bg-linear-to-b from-[#101828]/80 via-[#101828]/70 to-transparent">
            {/* Add subtle pattern overlay */}
            <div className="absolute inset-0 opacity-20 bg-[url('/images/patterns/circuit-pattern.svg')] bg-repeat"></div>
          </div>
        </div>
        <div className="relative h-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="mb-4 rounded-full bg-[#00B0F0]/20 p-2 sm:p-3">
              <LuScale className="h-6 w-6 sm:h-8 sm:w-8 text-[#00B0F0]" />
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white drop-shadow-lg">
              Terms of Service
            </h1>
            <p className="mt-2 sm:mt-4 text-sm sm:text-base md:text-lg text-gray-200 max-w-2xl">
              The rules and guidelines for using our services
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Last Updated Section */}
        <div className="mb-8 text-center">
          <p className="text-sm text-gray-500">Last Updated: April 2, 2025</p>
        </div>

        {/* Introduction */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Introduction</h2>
          <p className="text-gray-600 mb-4">
            Welcome to Qwetulands. These terms and conditions outline the rules and regulations for the use of our website and services.
          </p>
          <p className="text-gray-600">
            By accessing this website, we assume you accept these terms and conditions in full. Do not continue to use Qwetulands's website if you do not accept all of the terms and conditions stated on this page.
          </p>
        </div>

        {/* Definitions */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Definitions</h2>
          <p className="text-gray-600 mb-4">
            The following terminology applies to these Terms and Conditions, Privacy Statement, and any or all Agreements:
          </p>
          <ul className="list-none space-y-3">
            {[
              `"Client", "You" and "Your" refers to you, the person accessing this website and accepting the Company's terms and conditions.`,
              `"The Company", "Ourselves", "We", "Our" and "Us", refers to Qwetulands.`,
              `"Party", "Parties", or "Us", refers to both the Client and ourselves, or either the Client or ourselves.`,
              `"Services" refers to any services provided by Qwetulands including but not limited to Land Survey, GIS Mapping, Property Management, Cyber Services, Printing Services, and Qwetu Gas Supplier.`,
            ].map((item, index) => (
              <li key={index} className="flex items-start">
                <PiCheckCircleBold className="h-5 w-5 text-[#00B0F0] mr-2 mt-0.5 shrink-0" />
                <span className="text-gray-600">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* License */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">License</h2>
          <p className="text-gray-600 mb-4">
            Unless otherwise stated, Qwetulands and/or its licensors own the intellectual property rights for all material on this website. All intellectual property rights are reserved.
          </p>
          <p className="text-gray-600 mb-4">
            You may view and/or print pages from the website for your own personal use subject to restrictions set in these terms and conditions.
          </p>
          <p className="text-gray-600">You must not:</p>
          <ul className="list-none space-y-2 my-4">
            {[
              'Republish material from this website',
              'Sell, rent, or sub-license material from this website',
              'Reproduce, duplicate or copy material from this website',
              'Redistribute content from Qwetulands (unless content is specifically made for redistribution)',
            ].map((item, index) => (
              <li key={index} className="flex items-start">
                <PiCheckCircleBold className="h-5 w-5 text-[#00B0F0] mr-2 mt-0.5 shrink-0" />
                <span className="text-gray-600">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* User Comments */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">User Comments</h2>
          <p className="text-gray-600 mb-4">
            Certain parts of this website offer the opportunity for users to post and exchange opinions, information, material, and data. Qwetulands does not screen, edit, publish or review Comments prior to their appearance on the website and Comments do not reflect the views or opinions of Qwetulands, its agents, or affiliates.
          </p>
          <p className="text-gray-600 mb-4">
            Comments reflect the view and opinion of the person who posts such view or opinion. To the extent permitted by applicable laws, Qwetulands shall not be responsible or liable for the Comments or for any loss cost, liability, damages, or expenses caused and or suffered as a result of any use of and/or posting of and/or appearance of the Comments on this website.
          </p>
          <p className="text-gray-600">
            Qwetulands reserves the right to monitor all Comments and to remove any Comments which it considers in its absolute discretion to be inappropriate, offensive, or otherwise in breach of these Terms and Conditions.
          </p>
        </div>

        {/* Service Terms */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Service Terms</h2>
          <p className="text-gray-600 mb-4">
            Our services are provided on an "as is" and "as available" basis. Qwetulands makes no representations or warranties of any kind, express or implied, as to the operation of our services, or the information, content, or materials included therein.
          </p>
          <p className="text-gray-600 mb-4">
            You expressly agree that your use of our services is at your sole risk. Qwetulands reserves the right to withdraw or amend our service, and any service or material we provide, in our sole discretion without notice. We will not be liable if for any reason all or any part of our service is unavailable at any time or for any period.
          </p>
          <h3 className="text-lg font-medium text-gray-700 mb-3 mt-6">Service Booking and Cancellation</h3>
          <p className="text-gray-600 mb-4">
            When booking our services, you agree to provide accurate and complete information. Qwetulands reserves the right to refuse service, terminate accounts, or cancel orders at our discretion.
          </p>
          <p className="text-gray-600">
            Cancellation policies vary by service type. Please contact us directly for specific cancellation terms related to your booked service.
          </p>
        </div>

        {/* Payment Terms */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Payment Terms</h2>
          <p className="text-gray-600 mb-4">
            All prices listed on our website are in Kenyan Shillings (KES) unless otherwise specified. Prices for our services are subject to change without notice.
          </p>
          <p className="text-gray-600 mb-4">
            Payment methods accepted include M-Pesa, bank transfer, and other methods as specified during the booking process. Payment is required in advance for most services unless otherwise agreed in writing.
          </p>
          <p className="text-gray-600">
            Qwetulands takes steps to ensure that all transactions are secure. However, we cannot guarantee that unauthorized third parties will never be able to defeat our security measures.
          </p>
        </div>

        {/* Limitation of Liability */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Limitation of Liability</h2>
          <p className="text-gray-600 mb-4">
            To the maximum extent permitted by applicable law, in no event shall Qwetulands, its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
          </p>
          <ul className="list-none space-y-2 mb-4">
            {[
              'Your access to or use of or inability to access or use the service',
              'Any conduct or content of any third party on the service',
              'Any content obtained from the service',
              'Unauthorized access, use, or alteration of your transmissions or content',
            ].map((item, index) => (
              <li key={index} className="flex items-start">
                <PiCheckCircleBold className="h-5 w-5 text-[#00B0F0] mr-2 mt-0.5 shrink-0" />
                <span className="text-gray-600">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Governing Law */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Governing Law</h2>
          <p className="text-gray-600 mb-4">
            These Terms shall be governed and construed in accordance with the laws of Kenya, without regard to its conflict of law provisions.
          </p>
          <p className="text-gray-600">
            Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect.
          </p>
        </div>

        {/* Changes to Terms */}
        <div className="mb-10 bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Changes to Terms</h2>
          <p className="text-gray-600 mb-4">
            We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
          </p>
          <p className="text-gray-600">
            By continuing to access or use our service after any revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, you are no longer authorized to use the service.
          </p>
        </div>

        {/* Contact Us */}
        <div className="bg-white rounded-xl shadow-xs p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">Contact Us</h2>
          <p className="text-gray-600 mb-4">
            If you have any questions about these Terms, please contact us at:
          </p>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-gray-700 font-medium">Qwetulands</p>
            <p className="text-gray-600">Malindi, Opposite Mbuzi Wengi</p>
            <p className="text-gray-600">Email: <a href="mailto:info@qwetulands.com" className="text-[#00B0F0] hover:underline">info@qwetulands.com</a></p>
            <p className="text-gray-600">Phone: <a href="tel:+254756211546" className="text-[#00B0F0] hover:underline">+254 756 211 546</a></p>
          </div>
        </div>

        {/* Back to Home Button */}
        <div className="mt-10 text-center">
          <Link 
            href="/"
            className="inline-flex items-center justify-center rounded-lg bg-[#00B0F0] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[#0090D0] focus:outline-hidden focus:ring-2 focus:ring-[#00B0F0] focus:ring-offset-2"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
